package com.siriusxm.pia.views.unifiedaggregator

/**
 * A tab address on an entity detail page.
 *
 * Built-in tabs are closed and compiler-checked. Runtime enhancer tabs remain representable through
 * [Extension], without making the built-in destinations untyped.
 */
sealed interface EntityTab {
    val id: String

    enum class BuiltIn(override val id: String) : EntityTab {
        Removed("removed"),
        Atlas("outgoing"),
        Catalog("catalog"),
        Incoming("incoming"),
        Events("events"),
        AccessControl("access"),
        Relationships("relationships"),
        Color("color"),
        Services("services"),
        Transcript("transcript");

        companion object {
            fun fromId(id: String?): BuiltIn? = entries.find { it.id == id }
        }
    }

    data class Extension(override val id: String) : EntityTab

    companion object {
        fun fromId(id: String?): EntityTab? {
            val value = id?.takeIf { it.isNotBlank() } ?: return null
            return BuiltIn.fromId(value) ?: Extension(value)
        }
    }
}

/** The closed set of tabs on an entity-type detail page. */
enum class EntityTypeTab(val id: String) {
    Definition("definition"),
    Roles("roles"),
    Events("events"),
    Metrics("metrics");

    companion object {
        fun fromId(id: String?): EntityTypeTab? = entries.find { it.id == id }
    }
}

/**
 * The typed destination for the entity detail page touched by UCA-1039.
 *
 * It owns raw path segments only; [RouteLocation] applies encoding centrally, while event filters
 * remain query state supplied at navigation time.
 */
data class EntityDetailDestination(
    val id: String,
    val tab: EntityTab? = null
) : NavKey {
    override fun location(): RouteLocation =
        RouteLocation(listOf("entity", id) + listOfNotNull(tab?.id))
}

/** The typed destination for an entity-type detail page and its selected tab. */
data class EntityTypeDetailDestination(
    val entityType: String,
    val tab: EntityTypeTab? = null
) : NavKey {
    override fun location(): RouteLocation =
        RouteLocation(listOf("types", "entities", entityType) + listOfNotNull(tab?.id))
}
